import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import Rating from "@/components/base/Rating";
import StatusBadge from "@/components/base/StatusBadge";
import EmptyState from "@/components/base/EmptyState";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import { getAdminReviews, moderateReview } from "@/services/adminApi";
import type { Review } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "published", label: "Published" },
  { value: "hidden", label: "Hidden" },
];

export default function AdminReviews() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState<{ review: Review; action: "reject" | "delete" } | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminReviews(token)
      .then((r) => active && setRows(r))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const statusOf = (r: Review) => r.status ?? "published";

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || statusOf(r) === status;
      const matchesQuery =
        !q ||
        r.businessName.toLowerCase().includes(q) ||
        r.authorName.toLowerCase().includes(q) ||
        r.comment.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      pending: rows.filter((r) => statusOf(r) === "pending").length,
      published: rows.filter((r) => statusOf(r) === "published").length,
      hidden: rows.filter((r) => statusOf(r) === "hidden").length,
    }),
    [rows],
  );

  const approve = async (r: Review) => {
    setBusy(r.id);
    const ok = await moderateReview(r.id, "approve", token);
    setBusy(null);
    if (!ok) {
      flash("We couldn't approve that review. Please try again.");
      return;
    }
    setRows((prev) => prev.map((row) => (row.id === r.id ? { ...row, status: "published" } : row)));
    flash("Review approved and published.");
  };

  const reject = async (r: Review) => {
    setBusy(r.id);
    const ok = await moderateReview(r.id, "reject", token);
    setBusy(null);
    if (!ok) {
      flash("We couldn't reject that review. Please try again.");
      return;
    }
    setRows((prev) => prev.map((row) => (row.id === r.id ? { ...row, status: "hidden" } : row)));
    flash("Review rejected and hidden from the site.");
  };

  const remove = async (r: Review) => {
    setBusy(r.id);
    const ok = await moderateReview(r.id, "delete", token);
    setBusy(null);
    if (!ok) {
      flash("We couldn't remove that review. Please try again.");
      return;
    }
    setRows((prev) => prev.filter((row) => row.id !== r.id));
    flash("Review removed from the platform.");
  };

  return (
    <>
      <Seo title="Reviews" path="/admin/reviews" />

      <AdminPageHeader
        icon="ri-star-line"
        title="Reviews moderation"
        subtitle="Approve, hide or remove reviews. Reported and spammy submissions are queued here first."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search reviews, businesses, authors…">
          <AdminFilterTabs
            options={statusTabs.map((t) => ({
              value: t.value,
              label: `${t.label} (${counts[t.value as keyof typeof counts] ?? 0})`,
            }))}
            value={status}
            onChange={setStatus}
          />
        </AdminToolbar>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
            ))}
          </div>
        ) : error ? (
          <EmptyState icon="ri-wifi-off-line" title="Couldn't load reviews" description="Please refresh and try again." />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-star-line" title="No reviews match" description="Try a different status filter or search term." />
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <article key={r.id} className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-foreground-950">{r.authorName}</span>
                    <Rating value={r.rating} />
                    <StatusBadge status={statusOf(r)} />
                    {typeof r.reports === "number" && r.reports > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-bold text-secondary-900">
                        <i className="ri-flag-line"></i> {r.reports} report{r.reports > 1 ? "s" : ""}
                      </span>
                    )}
                    <span className="text-xs text-foreground-400">· {r.date}</span>
                  </div>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{r.comment}</p>
                  <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-store-2-line"></i> {r.businessName}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {statusOf(r) !== "published" && (
                    <button
                      type="button"
                      onClick={() => approve(r)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1 rounded-md border border-accent-300 px-3 py-1.5 text-xs font-semibold text-accent-800 hover:bg-accent-50 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-check-line"></i> Approve
                    </button>
                  )}
                  {statusOf(r) !== "hidden" && (
                    <button
                      type="button"
                      onClick={() => setPending({ review: r, action: "reject" })}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:bg-background-100 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-eye-off-line"></i> Reject
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setPending({ review: r, action: "delete" })}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1 rounded-md border border-secondary-300 px-3 py-1.5 text-xs font-semibold text-secondary-800 hover:bg-secondary-50 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i> Remove
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.action === "delete" ? "Remove this review?" : "Reject this review?"}
        message={
          pending?.action === "delete"
            ? `“${pending?.review.authorName}”'s review will be permanently deleted and the listing rating recalculated. This cannot be undone.`
            : `“${pending?.review.authorName}”'s review will be hidden from the public site. You can publish it again later.`
        }
        confirmLabel={pending?.action === "delete" ? "Delete review" : "Reject review"}
        tone={pending?.action === "delete" ? "danger" : "primary"}
        busy={Boolean(busy)}
        onConfirm={() => {
          if (!pending) return;
          const op = pending.action === "delete" ? remove : reject;
          const target = pending.review;
          setPending(null);
          void op(target);
        }}
        onCancel={() => setPending(null)}
      />
    </>
  );
}